import { Switch, Route } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { useEffect, useState } from "react";
import { checkAuthStatus } from "@/utils/auth";
import { ThemeProvider } from "@/components/layout/theme-provider";
import "@/i18n";
import AuthContext from "@/context/AuthContext";

import HomePage from "@/pages/home-page";
import TermsConditionsPage from "@/pages/terms-conditions";
import PrivacyPolicyPage from "@/pages/privacy-policy";
import NotFound from "@/pages/not-found";
import FreeTrialPage from "@/pages/free-trial";
import ContactSalesPage from "@/pages/contact-sales";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import FireModelDetails from "./pages/Models-details/fireDetails";
import FallModelDetails from "./pages/Models-details/fallDetails";
import ViolenceModelDetails from "./pages/Models-details/violenceDetails";
import ShopliftModelDetails from "./pages/Models-details/shopliftDetails";
import AccuracyFeature from "@/pages/features/accuracy";
import TechnologyFeature from "@/pages/features/technology";
import ExpertiseFeature from "@/pages/features/expertise";
import SupportFeature from "@/pages/features/support";
import ScalabilityBenefit from "@/pages/benefits/scalability";
import AccessibilityBenefit from "@/pages/benefits/accessibility";
import CostEffectiveBenefit from "@/pages/benefits/cost-effective";
import AutomaticUpdatesBenefit from "@/pages/benefits/automatic-updates";
import SecurityBenefit from "@/pages/benefits/security";
import PerformanceBenefit from "@/pages/benefits/performance";
import FireCaseStudy from "@/pages/case-studies/fire-detection";
import FallCaseStudy from "@/pages/case-studies/fall-detection";
import ViolenceCaseStudy from "@/pages/case-studies/violence-detection";
import ShopliftingDetectionCaseStudy from "@/pages/case-studies/shoplifting-detection";
import AuthenticationFeature from "@/pages/features/authentication";
import MonitoringFeature from "@/pages/features/monitoring";
import CookiesConsent from "./components/CookiesConsent";
import CookiesPolicyPage from "./pages/cookies-policy";
import Careers from "./pages/careers";
import GatewayPage from "./pages/gateway";
import AboutUsPage from "./pages/about-us";
import TechnologyPartnersPage from "./pages/technology-partners";
import LoginPage from "@/pages/login";
import ForgotPasswordPage from "./pages/forgot-password";


interface RouterProps {
  isAuthenticated: boolean;
}

function Router({ isAuthenticated }: RouterProps) {
  return (
    <Switch> 
     <Route path="/" component={() => <HomePage isLoggedIn={isAuthenticated} />} />
      <Route path="/terms-conditions" component={TermsConditionsPage} />
      <Route path="/privacy-policy" component={PrivacyPolicyPage} />
      <Route path="/cookies-policy" component={CookiesPolicyPage} />
      <Route path="/free-trial" component={FreeTrialPage} />
      <Route path="/contact-sales" component={ContactSalesPage} />

      <Route path="/fire-model-details" component={FireModelDetails} />
      <Route path="/fall-model-details" component={FallModelDetails} />
      <Route path="/violence-model-details" component={ViolenceModelDetails} />
      <Route path="/shoplift-model-details" component={ShopliftModelDetails} />

      {/* Feature Detail Pages */}
      <Route path="/features/accuracy" component={AccuracyFeature} />
      <Route path="/features/technology" component={TechnologyFeature} />
      <Route path="/features/expertise" component={ExpertiseFeature} />
      <Route path="/features/support" component={SupportFeature} />
      <Route path="/features/authentication" component={AuthenticationFeature} />
      <Route path="/features/monitoring" component={MonitoringFeature} />

      {/* Benefit Detail Pages */}
      <Route path="/benefits/scalability" component={ScalabilityBenefit} />
      <Route path="/benefits/accessibility" component={AccessibilityBenefit} />
      <Route path="/benefits/cost-effective" component={CostEffectiveBenefit} />
      <Route path="/benefits/automatic-updates" component={AutomaticUpdatesBenefit} />
      <Route path="/benefits/security" component={SecurityBenefit} />
      <Route path="/benefits/performance" component={PerformanceBenefit} />

      {/* case-studies Detail Pages */}
      <Route path="/case-studies/fire-detection" component={FireCaseStudy} />
      <Route path="/case-studies/fall-detection" component={FallCaseStudy} />
      <Route path="/case-studies/violence-detection" component={ViolenceCaseStudy} />
      <Route path="/case-studies/shoplifting-detection" component={ShopliftingDetectionCaseStudy} />

      {/* careers Detail Pages */}
      <Route path="/careers" component={Careers} />{/* Gateway Page */}
      <Route path="/gateway" component={GatewayPage} />

      {/* Technology Partners */}
      <Route path="/technology-partners" component={TechnologyPartnersPage} />

      {/* About Us */}
      <Route path="/about-us" component={AboutUsPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check authentication status on initial load and set up listener for auth changes
  useEffect(() => {
    // Initial check
    const checkAuth = async () => {
      const isAuth = await checkAuthStatus('app-init');
      setIsAuthenticated(isAuth);
      setIsLoading(false);
    };
    checkAuth();

    // Set up storage event listener to sync auth state across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth-status') {
        setIsAuthenticated(e.newValue === 'true');
      }
    };

    // Set up visibility change handler to check auth status when the page becomes visible
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        const isAuthenticated = await checkAuthStatus('visibility-change');
        setIsAuthenticated(isAuthenticated);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle route protection
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    if (isAuthenticated && currentPath === '/login') {
      navigate('/');
    }
  }, [isAuthenticated, window.location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
      <ThemeProvider defaultTheme="light">
        <div className="min-h-screen bg-white dark:bg-gray-900">
          <Router isAuthenticated={isAuthenticated} />
          <CookiesConsent />
          <LanguageSwitcher />
        </div>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;
